import React from 'react';
import {
  useToast as useChakraToast,
  ToastPosition,
  Flex,
  Box,
  Button,
} from '@chakra-ui/react';
import { CheckOutlineCircleIcon } from '@allergan-data-labs/alle-elements-icons/24/checkOutlineCircleIcon';
import { AlertOutlineIcon } from '@allergan-data-labs/alle-elements-icons/24/alertOutlineIcon';
import { ErrorNoIcon } from '@allergan-data-labs/alle-elements-icons/24/errorNoIcon';
import {
  getToastFlexStyle,
  getToastRenderFlexStyle,
  getToastVariantStyles,
} from './toast.theme';
import { ColorMode } from '@allergan-data-labs/alle-elements-core';
import {
  ButtonProps,
  getVariantStyles,
} from '@allergan-data-labs/alle-elements-button';

type ToastRenderFunction = (onClose?: () => void) => React.ReactNode;

export type ToastVariant = 'alert' | 'error' | 'success' | 'neutral';

export interface ToastProps {
  /**
   * Callback function to run side effects after the toast has closed.
   */
  onCloseComplete?: () => void;
  /**
   * The variant of the the Toast
   * @default 'success'
   */
  variant?: ToastVariant;
  /**
   * The delay before the toast hides (in milliseconds) If set to `null`, toast will never dismiss.
   * @default 5000 ( = 5000ms )
   */
  duration?: number | null;
  /**
   * The placement of the toast
   * @default "top"
   */
  position?: ToastPosition;
  /**
   * Render a component in Toast body.
   * Any component passed will receive  `onClose`.
   */
  render?(onClose?: () => void): React.ReactNode;
  /**
   * If true, toast will show a dismiss button
   *  @default false
   */
  isClosable?: boolean;
  /** The color mode that the toast will display.
   * @default light
   */
  colorMode?: ColorMode;
  /**
   * The icon to display in the Toast, if not provided, the variant's default icon will be used.
   */
  icon?: React.ReactElement;
  /**
   * If false, the icon will be hidden.
   * @default true
   */
  showIcon?: boolean;
  /**
   * Props for the dismiss button.
   * @required if `isClosable` is true
   */
  dismissButtonProps?: ButtonProps;
}

export const getToastButtonColorMode = (
  variant: ToastVariant,
  colorMode: ColorMode
): ColorMode => {
  if (variant === 'success' || variant === 'error') {
    return colorMode === 'light' ? 'dark' : 'light';
  }

  return colorMode;
};

export const useToast = () => {
  const chakraToast = useChakraToast();

  const ToastIcon = ({
    variant,
    iconProp,
  }: {
    variant: ToastVariant;
    iconProp?: React.ReactElement;
  }) => {
    const dataTestAttribute = { 'data-testid': `toast-icon-${variant}` };

    const defaultIcons = {
      alert: <AlertOutlineIcon {...dataTestAttribute} />,
      success: <CheckOutlineCircleIcon {...dataTestAttribute} />,
      error: <ErrorNoIcon {...dataTestAttribute} />,
      neutral: <CheckOutlineCircleIcon {...dataTestAttribute} />,
    };

    const IconComponent = iconProp
      ? React.cloneElement(iconProp, dataTestAttribute)
      : defaultIcons[variant];

    return IconComponent;
  };

  return (useToastProps?: {
    onCloseComplete?: () => void;
    variant?: ToastVariant;
    duration?: number | null;
    position?: ToastPosition;
    colorMode?: ColorMode;
    render?: ToastRenderFunction;
    isClosable?: boolean;
    icon?: React.ReactElement;
    showIcon?: boolean;
    dismissButtonProps?: ButtonProps;
  }) => {
    const {
      duration = 5000,
      position = 'top',
      variant = 'success',
      colorMode = 'light',
      render,
      isClosable = false,
      onCloseComplete,
      icon,
      showIcon = true,
      dismissButtonProps,
    } = useToastProps || {};

    const {
      onClick: dismissButtonOnclick,
      children: dismissButtonChildren,
      ...restDismissButtonProps
    } = dismissButtonProps || {};

    chakraToast({
      duration,
      position,
      status: 'success',
      containerStyle: getToastVariantStyles(variant, colorMode),
      render: ({ onClose }) => (
        <Flex sx={getToastFlexStyle()} data-status={variant}>
          {showIcon && (
            <ToastIcon variant={variant as ToastVariant} iconProp={icon} />
          )}
          <Box sx={getToastRenderFlexStyle()}>{render && render(onClose)}</Box>
          {isClosable && (
            <Button
              data-testid="dismiss-toast-button"
              aria-label="dismiss-toast-button"
              id="dismiss-toast-button"
              size="xs"
              sx={getVariantStyles(
                'ghost',
                'action',
                getToastButtonColorMode(variant, colorMode)
              )}
              onClick={(e) => {
                onClose();
                dismissButtonOnclick?.(e);
              }}
              {...restDismissButtonProps}
            >
              {dismissButtonChildren || 'Dismiss'}
            </Button>
          )}
        </Flex>
      ),
      onCloseComplete,
    });
  };
};

export type { ToastPosition } from '@chakra-ui/react';
